import { FC, useCallback, useEffect, useRef, useState } from "react";

import pickBy from "lodash/pickBy";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import useStoresList from "sections/storesPage/useStoresList";

import Loader from "components/Loader";
import NoItems from "components/NoItems";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import StoreTile from "components/StoreTile";
import CustomSearch from "components/CustomSearch";

import { STORE_FILTERS } from "enums/storeFilters";

import { Client } from "services/store/Store.interface";

import styles from "./styles";

const StoresSection: FC = () => {
  const [filter, setFilter] = useState<Record<string, string | boolean>>({
    "filters[search]": "",
    [`filters[${STORE_FILTERS.IS_ACTIVE}]`]: false,
    [`filters[${STORE_FILTERS.IS_STARTING_SOON}]`]: false,
    [`filters[${STORE_FILTERS.IS_ARCHIVE}]`]: false,
  });

  const onFilterChange = useCallback(
    (filterName: string, value: string | boolean) => {
      setFilter((prevState) => {
        return {
          ...prevState,
          [`filters[${filterName}]`]: value,
        };
      });
    },
    [],
  );

  const truthyFilters = pickBy(filter, (value) => !!value);

  const { items, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useStoresList({
      limit: 8,
      additionalFilters: truthyFilters,
    });

  useEffect(() => {
    window.history.scrollRestoration = "manual";
  }, []);

  const wrapperEl = useRef<HTMLDivElement | null>(null);

  const onSearch = useCallback(
    (value: string) => {
      const data = value.length >= 2 ? value : "";
      onFilterChange(STORE_FILTERS.SEARCH, data);
    },
    [onFilterChange],
  );

  useEffect(() => {
    if (!wrapperEl.current || !hasNextPage || isFetching) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchNextPage();
      }
    });
    observer.observe(wrapperEl.current);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetching]);

  return (
    <>
      <Container
        sx={{
          flex: 1,
          position: "relative",
        }}
      >
        <Box my={3} display="flex" alignItems="center" flexDirection="column">
          <Box
            sx={{
              width: {
                xs: "100%",
                md: "50%",
              },
              textAlign: "center",
            }}
          >
            <CustomSearch
              placeholder="SEARCH STORES"
              onSearch={onSearch}
              defaultValue=""
            />
          </Box>
        </Box>
      </Container>
      <Box
        sx={{
          mx: "auto",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <FormControlLabel
          sx={styles.checkbox}
          control={<Checkbox checkedIcon={<FiberManualRecordIcon />} />}
          label="Active"
          onChange={(_, isChecked) => {
            onFilterChange(STORE_FILTERS.IS_ACTIVE, isChecked);
          }}
        />
        <Box sx={{ px: 3 }}>
          <FormControlLabel
            sx={styles.checkbox}
            control={<Checkbox checkedIcon={<FiberManualRecordIcon />} />}
            label="Starting soon"
            onChange={(_, isChecked) => {
              onFilterChange(STORE_FILTERS.IS_STARTING_SOON, isChecked);
            }}
          />
        </Box>
        <FormControlLabel
          sx={styles.checkbox}
          control={<Checkbox checkedIcon={<FiberManualRecordIcon />} />}
          label="Archive"
          onChange={(_, isChecked) => {
            onFilterChange(STORE_FILTERS.IS_ARCHIVE, isChecked);
          }}
        />
      </Box>
      <Box sx={{ paddingTop: 3, opacity: 0.5 }}>
        <hr />
      </Box>
      <Box width="100%" py={4}>
        {isLoading && (
          <GridContainer>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((_, id) => (
              <GridItem xs={12} sm={4} md={3} key={`item-${id}`}>
                <StoreTile
                  client={{ name: "\u00A0" } as Client}
                  showSoldOut={false}
                />
              </GridItem>
            ))}
            <GridItem xs={12} mt={4}>
              <Loader />
            </GridItem>
          </GridContainer>
        )}
        {!isLoading && items.length === 0 && (
          <NoItems>
            <Typography variant="h2" gutterBottom>
              Sorry, there are no stores yet.
            </Typography>
          </NoItems>
        )}
        <GridContainer>
          {items.map((client, id) => (
            <GridItem xs={12} sm={4} md={3} key={`item-${id}`}>
              <StoreTile client={client} />
            </GridItem>
          ))}
          {isFetching && (
            <GridItem xs={12} mt={6}>
              <Loader />
            </GridItem>
          )}
        </GridContainer>
        <Box ref={wrapperEl} />
      </Box>
    </>
  );
};

export default StoresSection;
